import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { timeslotActions } from "../slices/timeslot";
import { getErrorMessage } from "../../utils/errorUtils";
import { TimeslotRequestDto } from "../../shared/dtos/timeslot/TimeslotRequestDto";
import { TimeslotResponseDto } from "../../shared/dtos/timeslot/TimeslotResponseDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const createTimeslot =
  (values: TimeslotRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(timeslotActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<TimeslotResponseDto>
      >("/timeslots", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Timeslot created successfully!");
        dispatch(timeslotActions.createSuccess(data.data));
        dispatch(
          timeslotActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(timeslotActions.setErrorCreate(message));
    }
  };

export const getTimeslots =
  (params?: { branchId?: string }) => async (dispatch: AppDispatch) => {
    dispatch(timeslotActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<TimeslotResponseDto[]>
      >("/timeslots", {
        headers: { requiresAuth: true },
        params,
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(timeslotActions.setItems(data.data));
      }
    } catch (error) {
      dispatch(timeslotActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getTimeslotById =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(timeslotActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<TimeslotResponseDto>
      >(`/timeslots/${id}`, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(timeslotActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(timeslotActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const updateTimeslot =
  (payload: { id: string; values: TimeslotRequestDto }) =>
  async (dispatch: AppDispatch) => {
    dispatch(timeslotActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<TimeslotResponseDto>
      >(`/timeslots/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Timeslot updated successfully!");
        dispatch(timeslotActions.updateSuccess(data.data));
        dispatch(
          timeslotActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(timeslotActions.setErrorUpdate(message));
    }
  };

export const deleteTimeslot = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(timeslotActions.setLoadingDelete());
  try {
    const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
      `/timeslots/${id}`,
      { headers: { requiresAuth: true } },
    );

    if (data.status === ApiResponseStatus.SUCCESS) {
      toast.success(data.message || "Timeslot deleted successfully!");
      dispatch(timeslotActions.deleteSuccess(id));
      dispatch(
        timeslotActions.setServerStatus({
          operation: "delete",
          status: data.statusCode || 200,
        }),
      );
    }
  } catch (error) {
    const message = getErrorMessage(error);
    toast.error(message);
    dispatch(timeslotActions.setErrorDelete(message));
  }
};

export const resetTimeslotServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(timeslotActions.setServerStatus(null));
};
